<template>
  <div class="px-3 pt-2 h-full overflow-auto pb-5 text-[13px] leading-normal text-dark-text">
    <!-- ─── Input Variable ─── -->
    <VariableSelect label="Input Variable" v-model="inputVariable" class="w-full mt-3" system :filter-data-type="['DataTable']" @update:modelValue="onPayloadChange" />
    <p v-if="submitted && !inputVariable" class="mt-1 text-12 text-alert font-medium">Input variable is required.</p>

    <!-- ─── Excel Template Variable ─── -->
    <VariableSelect label="Excel Template" v-model="outputVariable" class="w-full mt-3" system :filter-data-type="['Excel']" @update:modelValue="onPayloadChange" />
    <p v-if="submitted && !outputVariable" class="mt-1 text-12 text-alert font-medium">Excel template variable is required.</p>

    <div class="mt-3 px-4 py-3 border border-stroke rounded-t-md">
      <!-- ═══════════════════════════════════════════ -->
      <!-- MAPPING STRATEGY SELECTOR                   -->
      <!-- ═══════════════════════════════════════════ -->
      <div class="mb-3">
        <label class="block text-12 font-semibold text-gray-600 mb-1 uppercase tracking-wide">Mapping Strategy</label>
        <div class="grid grid-cols-2 gap-2">
          <button
            v-for="mode in mappingModes"
            :key="mode.value"
            type="button"
            class="text-left border rounded-md px-3 py-2 transition-colors duration-150 cursor-pointer"
            :class="mappingMode === mode.value ? 'border-secondary bg-secondary/10 text-dark-text' : 'border-stroke bg-white text-gray-600 hover:border-secondary/40'"
            @click="
              mappingMode = mode.value;
              onPayloadChange();
            "
          >
            <div class="flex items-start gap-2">
              <div class="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" :class="mappingMode === mode.value ? 'bg-secondary text-on-secondary' : 'bg-surface-dark text-gray-600'">
                <svg v-if="mode.value === 'sectioned'" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </div>
              <div>
                <div class="text-[12px] font-semibold leading-tight">{{ mode.label }}</div>
                <div class="text-11 opacity-70 leading-snug">{{ mode.description }}</div>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════ -->
      <!-- SECTIONED MODE CONFIG                       -->
      <!-- ═══════════════════════════════════════════ -->
      <template v-if="mappingMode === 'sectioned'">
        <!-- Section Detection -->
        <div class="border-t border-stroke pt-3 mb-3">
          <label class="block text-12 font-semibold text-gray-600 mb-1 uppercase tracking-wide">Section Detection</label>
          <p class="text-11 text-gray-500 mb-2">How the template identifies section boundaries (e.g. "Product Name" label rows)</p>

          <div class="grid grid-cols-3 gap-2 mb-2">
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Section Label Text</label>
              <input v-model="sectioned.sectionLabelText" class="edm-input" placeholder="e.g. Product Name" @input="onPayloadChange" />
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Label Column</label>
              <input v-model="sectioned.sectionLabelColumn" class="edm-input" placeholder="A or 1" @input="onPayloadChange" />
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Value Column</label>
              <input v-model="sectioned.sectionValueColumn" class="edm-input" placeholder="B or 2" @input="onPayloadChange" />
            </div>
          </div>

          <div>
            <label class="block text-12 font-medium text-gray-600 mb-1">Section Key Field (DataTable)</label>
            <input v-model="sectioned.sectionKeyField" class="edm-input" placeholder="e.g. Product Name" @input="onPayloadChange" />
            <span class="text-11 text-gray-500">DataTable field matching the section value</span>
          </div>
        </div>

        <!-- Row Matching & Value Insertion -->
        <div class="border-t border-stroke pt-3 mb-3">
          <label class="block text-12 font-semibold text-gray-600 mb-1 uppercase tracking-wide">Row Matching &amp; Value Insertion</label>
          <p class="text-11 text-gray-500 mb-2">How rows within each section match DataTable entries</p>

          <div class="grid grid-cols-3 gap-2 mb-2">
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Lookup Column (Excel)</label>
              <input v-model="sectioned.lookupColumn" class="edm-input" placeholder="A or 1" @input="onPayloadChange" />
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Lookup Field (DataTable)</label>
              <input v-model="sectioned.lookupField" class="edm-input" placeholder="e.g. Product Name" @input="onPayloadChange" />
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Value Field (DataTable)</label>
              <input v-model="sectioned.valueField" class="edm-input" placeholder="e.g. Product Name" @input="onPayloadChange" />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Output Column (Excel)</label>
              <input v-model="sectioned.outputColumn" class="edm-input" placeholder="D or 4" @input="onPayloadChange" />
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Worksheet Index</label>
              <input v-model.number="sectioned.worksheetIndex" type="number" min="0" class="edm-input" placeholder="0" @input="onPayloadChange" />
              <span class="text-11 text-gray-500">0-based sheet index</span>
            </div>
          </div>
        </div>

        <!-- Additional Value Columns -->
        <div class="border-t border-stroke pt-3 mb-3">
          <div class="flex items-center justify-between mb-2">
            <label class="block text-12 font-semibold text-gray-600 uppercase tracking-wide">Additional Value Columns</label>
            <button
              type="button"
              class="
                inline-flex
                items-center
                gap-1
                text-11
                font-semibold
                text-secondary
                bg-secondary/10
                hover:bg-secondary/20
                border border-secondary/30
                rounded
                px-2
                py-1
                cursor-pointer
                transition-colors
                duration-150
              "
              @click="addSectionedValueMapping"
            >
              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" /></svg>
              Add
            </button>
          </div>
          <p class="text-11 text-gray-500 mb-2">Map more DataTable fields to additional Excel columns in the same row</p>
          <div v-if="sectioned.additionalValues.length" class="space-y-2">
            <div v-for="(av, idx) in sectioned.additionalValues" :key="idx" class="flex items-end gap-2">
              <div class="flex-1">
                <label class="block text-12 font-medium text-gray-600 mb-1">DataTable Field</label>
                <input v-model="av.dataField" class="edm-input" placeholder="e.g. Product Name" @input="onPayloadChange" />
              </div>
              <div class="flex-1">
                <label class="block text-12 font-medium text-gray-600 mb-1">Excel Column</label>
                <input v-model="av.column" class="edm-input" placeholder="E or 5" @input="onPayloadChange" />
              </div>
              <button
                type="button"
                class="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded text-gray-500 hover:text-alert hover:bg-alert/10 transition-colors duration-150 cursor-pointer"
                @click="removeSectionedValueMapping(idx)"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <div v-else class="text-11 text-gray-500 italic">No additional value columns configured</div>
        </div>
      </template>

      <!-- ═══════════════════════════════════════════ -->
      <!-- TABULAR MODE CONFIG                         -->
      <!-- ═══════════════════════════════════════════ -->
      <template v-if="mappingMode === 'tabular'">
        <!-- Header & Data Rows -->
        <div class="border-t border-stroke pt-3 mb-3">
          <label class="block text-12 font-semibold text-gray-600 mb-1 uppercase tracking-wide">Header &amp; Data Rows</label>
          <div class="grid grid-cols-3 gap-2">
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Header Row</label>
              <input v-model.number="tabular.headerRow" type="number" min="1" class="edm-input" placeholder="3" @input="onPayloadChange" />
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Data Start Row</label>
              <input v-model.number="tabular.dataStartRow" type="number" min="1" class="edm-input" placeholder="5" @input="onPayloadChange" />
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Worksheet Index</label>
              <input v-model.number="tabular.worksheetIndex" type="number" min="0" class="edm-input" placeholder="0" @input="onPayloadChange" />
              <span class="text-11 text-gray-500">Used when sheet splitting is off</span>
            </div>
          </div>
        </div>

        <!-- Column Scanning -->
        <div class="border-t border-stroke pt-3 mb-3">
          <label class="block text-12 font-semibold text-gray-600 mb-1 uppercase tracking-wide">Column Scanning</label>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Start Column</label>
              <input v-model="tabular.startColumn" class="edm-input" placeholder="A or 1" @input="onPayloadChange" />
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Max Empty Gap</label>
              <input v-model.number="tabular.maxEmptyGap" type="number" min="1" max="50" class="edm-input" placeholder="5" @input="onPayloadChange" />
              <span class="text-11 text-gray-500">Stop after N consecutive empty headers</span>
            </div>
          </div>
        </div>

        <!-- Column Mapping Overrides -->
        <div class="border-t border-stroke pt-3 mb-3">
          <div class="flex items-center justify-between mb-2">
            <label class="block text-12 font-semibold text-gray-600 uppercase tracking-wide">Column Mapping Overrides</label>
            <button
              type="button"
              class="
                inline-flex
                items-center
                gap-1
                text-11
                font-semibold
                text-secondary
                bg-secondary/10
                hover:bg-secondary/20
                border border-secondary/30
                rounded
                px-2
                py-1
                cursor-pointer
                transition-colors
                duration-150
              "
              @click="addTabularOverride"
            >
              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" /></svg>
              Add
            </button>
          </div>
          <p class="text-11 text-gray-500 mb-2">Override auto-matching when Excel header names differ from DataTable field names</p>
          <div v-if="tabular.columnOverrides.length" class="space-y-2">
            <div v-for="(ov, idx) in tabular.columnOverrides" :key="idx" class="flex items-end gap-2">
              <div class="flex-1">
                <label class="block text-12 font-medium text-gray-600 mb-1">Excel Header</label>
                <input v-model="ov.excelHeader" class="edm-input" placeholder="Header text" @input="onPayloadChange" />
              </div>
              <div class="flex-1">
                <label class="block text-12 font-medium text-gray-600 mb-1">DataTable Field</label>
                <input v-model="ov.dataField" class="edm-input" placeholder="e.g. Product Name" @input="onPayloadChange" />
              </div>
              <button
                type="button"
                class="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded text-gray-500 hover:text-alert hover:bg-alert/10 transition-colors duration-150 cursor-pointer"
                @click="removeTabularOverride(idx)"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <div v-else class="text-11 text-gray-500 italic">No overrides — all columns matched by header name</div>
        </div>

        <!-- Data Insertion Options -->
        <div class="border-t border-stroke pt-3 mb-3">
          <label class="block text-12 font-semibold text-gray-600 mb-1 uppercase tracking-wide">Data Insertion</label>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Insert Direction</label>
              <select v-model="tabular.direction" class="edm-select" @change="onPayloadChange">
                <option value="rows">Row by row (↓ downward)</option>
                <option value="columns">Column by column (→ rightward)</option>
              </select>
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">On Missing Header</label>
              <select v-model="tabular.onMissingHeader" class="edm-select" @change="onPayloadChange">
                <option value="skip">Skip column</option>
                <option value="error">Throw error</option>
                <option value="insert_blank">Insert blank</option>
              </select>
            </div>
          </div>
        </div>

        <!-- ═══════════════════════════════════════════ -->
        <!-- SPLIT BY SHEET                              -->
        <!-- ═══════════════════════════════════════════ -->
        <div class="border-t border-stroke pt-3 mb-3">
          <div class="flex items-center gap-2 mb-2">
            <input
              id="edm-split-enabled"
              type="checkbox"
              v-model="tabular.splitBySheet.enabled"
              class="w-4 h-4 rounded border-stroke text-secondary focus:ring-secondary cursor-pointer"
              @change="onPayloadChange"
            />
            <label for="edm-split-enabled" class="text-12 font-semibold text-gray-600 uppercase tracking-wide cursor-pointer"> Split Output Into Multiple Sheets </label>
          </div>
          <p class="text-11 text-gray-500 mb-2">Route data rows to different worksheets by matching a DataTable field value to existing sheet names in the template</p>

          <template v-if="tabular.splitBySheet.enabled">
            <div class="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label class="block text-12 font-medium text-gray-600 mb-1">Split Field (DataTable)</label>
                <input v-model="tabular.splitBySheet.splitField" class="edm-input" placeholder="e.g. Product Name" @input="onPayloadChange" />
                <span class="text-11 text-gray-500">Field whose value matches sheet names</span>
              </div>
              <div>
                <label class="block text-12 font-medium text-gray-600 mb-1">Sheet Name Matching</label>
                <select v-model="tabular.splitBySheet.matchMode" class="edm-select" @change="onPayloadChange">
                  <option value="exact">Exact match</option>
                  <option value="contains">Sheet name contains value</option>
                  <option value="starts_with">Sheet name starts with value</option>
                </select>
              </div>
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">On No Matching Sheet</label>
              <select v-model="tabular.splitBySheet.onNoSheet" class="edm-select" @change="onPayloadChange">
                <option value="skip">Skip rows</option>
                <option value="error">Throw error</option>
                <option value="first_sheet">Write to first sheet</option>
              </select>
            </div>
          </template>
        </div>
      </template>

      <!-- ═══════════════════════════════════════════ -->
      <!-- ADVANCED OPTIONS (shared, collapsible)      -->
      <!-- ═══════════════════════════════════════════ -->
      <div class="border-t border-stroke pt-3">
        <button type="button" class="flex items-center gap-2 bg-transparent border-none cursor-pointer text-12 font-semibold text-gray-500 p-0 mb-0" @click="showAdvanced = !showAdvanced">
          <svg :class="['w-4 h-4 transition-transform duration-200', showAdvanced ? 'rotate-90' : '']" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
          </svg>
          Advanced Options
        </button>

        <div v-show="showAdvanced" class="mt-2 space-y-2">
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Trim Whitespace</label>
              <select v-model="advanced.trimWhitespace" class="edm-select" @change="onPayloadChange">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">Case-Insensitive Matching</label>
              <select v-model="advanced.caseInsensitive" class="edm-select" @change="onPayloadChange">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-12 font-medium text-gray-600 mb-1">On No Match Found</label>
              <select v-model="advanced.onNoMatch" class="edm-select" @change="onPayloadChange">
                <option value="skip">Leave cell unchanged</option>
                <option value="blank">Clear cell</option>
                <option value="placeholder">Insert placeholder text</option>
                <option value="error">Throw error</option>
              </select>
            </div>
            <div v-if="advanced.onNoMatch === 'placeholder'">
              <label class="block text-12 font-medium text-gray-600 mb-1">Placeholder Text</label>
              <input v-model="advanced.placeholderText" class="edm-input" placeholder="N/A" @input="onPayloadChange" />
            </div>
          </div>
          <div>
            <label class="block text-12 font-medium text-gray-600 mb-1">Preserve Formatting</label>
            <select v-model="advanced.preserveFormatting" class="edm-select" @change="onPayloadChange">
              <option :value="true">Yes — keep cell styles from template</option>
              <option :value="false">No — overwrite styles</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── JSON Preview (collapsible) ─── -->
    <div class="px-4 py-3 border-x border-b border-stroke rounded-b-lg">
      <button type="button" class="flex items-center gap-2 bg-transparent border-none cursor-pointer text-xs font-semibold text-gray-500 p-0" @click="showPreview = !showPreview">
        <svg :class="['w-4 h-4 transition-transform duration-200', showPreview ? 'rotate-90' : '']" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
        </svg>
        JSON Payload Preview
      </button>
      <pre v-if="showPreview" class="mt-2 p-3 bg-surface-dark border border-stroke rounded-md text-12 font-mono overflow-x-auto whitespace-pre leading-relaxed max-h-[400px] overflow-y-auto">{{
        payloadJson
      }}</pre>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import uuidv4 from '../../../helpers/uuid';
import EvaluateItemsConfig from './EvaluateItemsConfig';

/**
 * DataMapperExcelConfig — Vue 2 nocode block configuration component.
 *
 * Provides a UI for defining mapping rules that transform a DataTable (array of
 * objects) into a templated Excel file. Two mapping strategies are supported:
 *
 * - **Sectioned Lookup**: Walks Excel rows, detects section-label boundaries
 *   (e.g. "Product Name"), and fills matching rows within each section from the
 *   DataTable.
 *
 * - **Tabular Fill**: Reads column headers from a designated row, auto-maps or
 *   manually overrides header→field bindings, and fills data rows starting from
 *   a configured row. Optionally splits output across multiple worksheets by
 *   matching a DataTable field value to existing sheet names in the template.
 *
 * The emitted payload is consumed by the backend `executeExcelDataMapper` runtime
 * function, which performs the actual mapping using ExcelJS.
 *
 * @component
 */
export default {
  name: 'DataMapperExcelConfig',
  props: {
    item: { type: Object, default: null },
  },
  data() {
    let init;
    if (this.item) {
      init = JSON.parse(JSON.stringify(this.item.payload));
    } else {
      init = structuredClone(EvaluateItemsConfig['data-mapper-datatable-to-excel'].payload);
    }
    return {
      inputVariable: init?.inputVariable || '',
      outputVariable: init?.outputVariable || '',
      mappingMode: init?.mappingMode || 'sectioned',

      sectioned: {
        sectionLabelText: '',
        sectionLabelColumn: '1',
        sectionValueColumn: '2',
        sectionKeyField: '',
        lookupColumn: '1',
        lookupField: '',
        valueField: '',
        outputColumn: '4',
        worksheetIndex: 0,
        additionalValues: [],
        ...(init?.sectioned || {}),
      },

      tabular: {
        headerRow: 1,
        dataStartRow: 2,
        worksheetIndex: 0,
        startColumn: '1',
        maxEmptyGap: 5,
        direction: 'rows',
        onMissingHeader: 'skip',
        columnOverrides: [],
        ...(init?.tabular || {}),
        splitBySheet: {
          enabled: false,
          splitField: '',
          matchMode: 'exact',
          onNoSheet: 'skip',
          ...(init?.tabular?.splitBySheet || {}),
        },
      },

      advanced: {
        trimWhitespace: true,
        caseInsensitive: false,
        onNoMatch: 'skip',
        placeholderText: 'N/A',
        preserveFormatting: true,
        ...(init?.advanced || {}),
      },

      submitted: false,
      showPreview: false,
      showAdvanced: false,

      mappingModes: [
        {
          value: 'sectioned',
          label: 'Sectioned Lookup',
          description: 'Section labels group rows (e.g. Product → Features)',
        },
        {
          value: 'tabular',
          label: 'Tabular Fill',
          description: 'Header row maps columns, data fills below',
        },
      ],
    };
  },
  computed: {
    ...mapState(['projectData']),
    variables() {
      let variables = [];
      if (this.projectId && Object.keys(this.projectData).length) {
        variables = Object.keys(this.projectData?.[this.projectId]?.variables || {}).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) || [];
      }
      return variables.map((variable) => ({ id: uuidv4(), value: variable }));
    },
    projectId() {
      return this.$route.params.projectId;
    },
    payload() {
      return {
        id: this.item.id,
        type: this.item.type,
        payload: {
          inputVariable: this.inputVariable,
          outputVariable: this.outputVariable,
          mappingMode: this.mappingMode,
          sectioned: this.mappingMode === 'sectioned' ? JSON.parse(JSON.stringify(this.sectioned)) : undefined,
          tabular: this.mappingMode === 'tabular' ? JSON.parse(JSON.stringify(this.tabular)) : undefined,
          advanced: JSON.parse(JSON.stringify(this.advanced)),
        },
      };
    },
    payloadJson() {
      return JSON.stringify(this.payload, null, 2);
    },
  },
  methods: {
    ...mapActions(['showToastMessage']),
    onPayloadChange() {
      this.$nextTick(() => {
        this.submitted = true;
        this.$emit('update', this.payload);
      });
    },
    addSectionedValueMapping() {
      this.sectioned.additionalValues.push({ field: '', column: '' });
      this.onPayloadChange();
    },
    removeSectionedValueMapping(idx) {
      this.sectioned.additionalValues.splice(idx, 1);
      this.onPayloadChange();
    },
    addTabularOverride() {
      this.tabular.columnOverrides.push({ excelHeader: '', dataField: '' });
      this.onPayloadChange();
    },
    removeTabularOverride(idx) {
      this.tabular.columnOverrides.splice(idx, 1);
      this.onPayloadChange();
    },
  },
};
</script>

<style scoped>
.edm-input {
  @apply w-full text-[12px] border border-stroke rounded px-2 py-2 bg-white text-dark-text focus:outline-none focus:border-secondary;
}
.edm-input::placeholder {
  @apply text-gray-500;
}
.edm-select {
  @apply w-full text-[12px] border border-stroke rounded px-2 py-2 bg-white text-dark-text cursor-pointer focus:outline-none focus:border-secondary;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
  padding-right: 2rem;
}
</style>
